'use client';

import { Popover, Stack } from '@mui/material';
import type { BackupPostureControl, Condition } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { ConditionSelectionPanel, type SelectedConditionValue } from '@/components/conditions/conditionSelectionPanel';
import { useConditionPanel } from '@/components/conditions/useConditionPanel';
import { CheckboxTreeFilter } from '@/components/filters/checkboxTreeFilter';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { ControlsSeverity } from '@/data/inventory/data';
import { conditionValues, createValueCondition, findValueCondition, isValueCondition, type CompanionConditionComponentProps } from '@/types/advanceFilter';
import { ControlIndicator, controlKeysPriorities } from './controlIndicator';
import type { FilterProps } from '../queryBuilder/filters';
import { CONTROL_ID_PROPERTY, CONTROL_LEVEL_PROPERTY, MUTED_CONTROL_ID_PROPERTY } from '../queryBuilder/propertyNames';
const MUTED = 'muted';
interface ControlSeverityGroup {
  priority: string;
  title: string;
  color: string;
  controls: BackupPostureControl[];
}
const buildGroups = (controls: BackupPostureControl[]): ControlSeverityGroup[] => controlKeysPriorities.map(priority => ({
  priority,
  // "High severity" reads as a severity; "Muted" is a state, not a level.
  title: priority === MUTED ? ControlsSeverity[priority].title : `${ControlsSeverity[priority].title} severity`,
  color: ControlsSeverity[priority].color,
  // Muting cuts across severities, so that group offers every control — the
  // same set the accordion panel's muted drill-down lists.
  controls: [...(priority === MUTED ? controls : controls.filter(c => c.severity.toLowerCase() === priority))].sort((a, b) => a.name.localeCompare(b.name))
}));
const isControlCondition = (condition: Condition) => isValueCondition(condition) && (condition.property === CONTROL_LEVEL_PROPERTY || condition.property === CONTROL_ID_PROPERTY || condition.property === MUTED_CONTROL_ID_PROPERTY);
const buildControlConditions = (severities: string[], controls: string[], mutedControls: string[]): Condition[] => {
  const out: Condition[] = [];
  if (severities.length) {
    out.push(createValueCondition(CONTROL_LEVEL_PROPERTY, severities));
  }
  if (controls.length) {
    out.push(createValueCondition(CONTROL_ID_PROPERTY, controls));
  }
  if (mutedControls.length) {
    out.push(createValueCondition(MUTED_CONTROL_ID_PROPERTY, mutedControls));
  }
  return out;
};

/**
 * Violated controls, by severity. A severity row filters on the level itself;
 * the controls inside it filter on individual control ids — `controls` for a
 * real severity and `mutedControls` under Muted, which is what the accordion
 * panel's Backup status drill-down writes for the same picks.
 */
const ViolatedControlsFilter = ({
  conditions,
  onChange
}: FilterProps) => {
  const dal = useDAL();
  const {
    data: body,
    isLoading
  } = dal.controls.useList();
  const groups = useMemo(() => buildGroups(body?.backupPostureControls ?? []), [body?.backupPostureControls]);
  if (isLoading) {
    return <Loader />;
  }
  const valuesOf = (property: string) => conditionValues(findValueCondition(conditions, property));
  const selectedSeverities = valuesOf(CONTROL_LEVEL_PROPERTY);
  const selectedControls = valuesOf(CONTROL_ID_PROPERTY);
  const selectedMuted = valuesOf(MUTED_CONTROL_ID_PROPERTY);
  const apply = (severities: string[], controls: string[], mutedControls: string[]) => {
    onChange([...conditions.filter(c => !isControlCondition(c)), ...buildControlConditions(severities, controls, mutedControls)]);
  };
  const selectionOf = (group: ControlSeverityGroup) => group.priority === MUTED ? selectedMuted : selectedControls;
  const applyGroupSelection = (group_0: ControlSeverityGroup, severities_0: string[], selection: string[]) => group_0.priority === MUTED ? apply(severities_0, selectedControls, selection) : apply(severities_0, selection, selectedMuted);
  const onParentChange = (group_1: ControlSeverityGroup) => {
    const level = group_1.priority.toUpperCase();
    const nextSeverities = selectedSeverities.includes(level) ? selectedSeverities.filter(s => s !== level) : [...selectedSeverities, level];
    const groupControlIds = group_1.controls.map(c_0 => c_0.id);
    applyGroupSelection(group_1, nextSeverities, selectionOf(group_1).filter(id => !groupControlIds.includes(id)));
  };
  const onChildChange = (group_2: ControlSeverityGroup, controlId: string) => {
    const level_0 = group_2.priority.toUpperCase();
    const nextSeverities_0 = selectedSeverities.filter(s_0 => s_0 !== level_0);
    const selected = selectionOf(group_2);

    // Picking one control while the whole severity is selected narrows the
    // filter to that control, the same swap the data-classes tree makes.
    if (selectedSeverities.includes(level_0)) {
      const groupControlIds_0 = group_2.controls.map(c_1 => c_1.id);
      applyGroupSelection(group_2, nextSeverities_0, [...selected.filter(id_0 => !groupControlIds_0.includes(id_0)), controlId]);
      return;
    }
    applyGroupSelection(group_2, nextSeverities_0, selected.includes(controlId) ? selected.filter(id_1 => id_1 !== controlId) : [...selected, controlId]);
  };
  const byId = new Map(groups.map(group_3 => [group_3.priority, group_3]));
  return <CheckboxTreeFilter groupTestIdPrefix='filter-severity-' childTestIdPrefix='filter-control-'
  // Control names are sentences, so they wrap rather than truncate.
  wrapChildLabels groups={groups.map(group_4 => ({
    id: group_4.priority,
    label: group_4.title,
    selected: selectedSeverities.includes(group_4.priority.toUpperCase()),
    adornment: <ControlIndicator backgroundColor={group_4.color} />,
    children: group_4.controls.map(control => ({
      id: control.id,
      label: control.name,
      selected: selectionOf(group_4).includes(control.id),
      adornment: <ControlIndicator backgroundColor={group_4.color} />
    }))
  }))} onGroupToggle={groupId => onParentChange(byId.get(groupId)!)} onChildToggle={(groupId_0, controlId_0) => onChildChange(byId.get(groupId_0)!, controlId_0)} />;
};

/**
 * The filter bar's violated-controls chip. Needs the chip's whole condition set
 * because a severity, a control and a muted control are three different
 * properties on the wire.
 */
export const ViolatedControlsCondition = ({
  property,
  conditions,
  onChange,
  panelHost
}: CompanionConditionComponentProps) => {
  const panel = useConditionPanel(panelHost);
  const dal = useDAL();
  const {
    data: body
  } = dal.controls.useList();
  const labelOf = ({
    property: name,
    value
  }: SelectedConditionValue) => name === CONTROL_LEVEL_PROPERTY ? `${ControlsSeverity[value.toLowerCase()]?.title ?? value} severity` : body?.backupPostureControls.find(c => c.id === value)?.name ?? value;
  return <Popover open={panel.isOpen} anchorEl={panel.anchorEl} onClose={panel.close} anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left'
  }} transformOrigin={{
    vertical: 'top',
    horizontal: 'left'
  }} slotProps={{
    paper: {
      sx: {
        marginTop: '4px'
      }
    }
  }}>
      <Stack className='w-[308px]'>
        <ConditionSelectionPanel conditions={conditions} onConditionsChange={onChange} entityName='control' labelOf={labelOf}>
          <Stack className='overflow-y-auto px-[24px]'>
            <ViolatedControlsFilter property={property} conditions={conditions} onChange={onChange} />
          </Stack>
        </ConditionSelectionPanel>
      </Stack>
    </Popover>;
};